import { useMemo, useState } from 'react'
import { Alert, Group, Loader, SegmentedControl, Select, Stack, Tabs, Text, Title } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { fluxoApi, type AbaDoFluxo } from '@/api/chamados'
import { useCan } from '@/store/auth'
import { ComoOPrazoCorre } from './ComoOPrazoCorre'
import { DetalheDoFluxo, type Selecao } from './DetalheDoFluxo'
import { DiagramaDoFluxo } from './DiagramaDoFluxo'
import { ListaDoFluxo } from './ListaDoFluxo'
import { filtrarPorVisao, type Visao } from './visaoDoFluxo'

type Modo = 'lista' | 'diagrama'

interface Candidata { id: number; name: string }

/** A Organização a escolher quando a pessoa é de mais de uma (400 `multiple_organizations`). */
function candidatasDoErro(erro: unknown): Candidata[] | null {
  if (!isAxiosError(erro) || erro.response?.status !== 400) return null
  const corpo = erro.response.data as { detail?: string; candidates?: Candidata[] }
  return corpo?.detail === 'multiple_organizations' ? corpo.candidates ?? [] : null
}

/**
 * Como funciona o chamado e o SLA (spec 2026-10-26-chamados-como-funciona §3). Tudo o que a
 * tela desenha vem do backend, que lê do mesmo código que decide as transições.
 */
export function ComoFunciona() {
  const can = useCan()
  const atende = can('chamados', 'assign') || can('chamados', 'approve')
  const celular = useMediaQuery('(max-width: 62em)') ?? false
  const [visao, setVisao] = useState<Visao>(atende ? 'atendimento' : 'solicitante')
  const [aba, setAba] = useState<AbaDoFluxo>('atendimento')
  const [modoEscolhido, setModo] = useState<Modo | null>(null)
  const [organizationId, setOrganizationId] = useState<number | undefined>()
  const [selecao, setSelecao] = useState<Selecao | null>(null)
  const modo: Modo = modoEscolhido ?? (celular ? 'lista' : 'diagrama')
  const abaVisivel: AbaDoFluxo = visao === 'atendimento' ? aba : 'atendimento'

  const mapa = useQuery({ queryKey: ['chamados-fluxo-mapa'], queryFn: () => fluxoApi.mapa().then((r) => r.data) })
  const numeros = useQuery({
    queryKey: ['chamados-fluxo-agora', organizationId],
    queryFn: () => fluxoApi.agora({ organizationId }).then((r) => r.data),
    retry: false,
  })
  const candidatas = candidatasDoErro(numeros.error)

  const fluxo = useMemo(
    () => (mapa.data ? filtrarPorVisao(mapa.data, visao, abaVisivel) : null),
    [mapa.data, visao, abaVisivel],
  )

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Title order={2}>Como funciona o chamado</Title>
        <Text style={{ color: 'var(--ai-text-secondary)' }}>
          Por onde um chamado passa, quem move cada passo, o que o sistema faz sozinho e como o prazo corre.
        </Text>
      </Stack>

      <Group gap="md" wrap="wrap" align="flex-end">
        <Stack gap={4}>
          <Text size="sm" fw={600}>Ver como</Text>
          <SegmentedControl value={visao} onChange={(v) => { setVisao(v as Visao); setSelecao(null) }}
            data={[{ value: 'solicitante', label: 'Solicitante' }, { value: 'atendimento', label: 'Atendimento' }]} />
        </Stack>
        <Stack gap={4}>
          <Text size="sm" fw={600}>Mostrar como</Text>
          <SegmentedControl value={modo} onChange={(v) => setModo(v as Modo)}
            data={[{ value: 'lista', label: 'Lista' }, { value: 'diagrama', label: 'Diagrama' }]} />
        </Stack>
        {candidatas && (
          <Select label="Organização" placeholder="Escolha a Organização" w={260}
            data={candidatas.map((c) => ({ value: String(c.id), label: c.name }))}
            value={organizationId ? String(organizationId) : null}
            onChange={(v) => setOrganizationId(v ? Number(v) : undefined)} />
        )}
      </Group>

      {mapa.isLoading && <Loader size="sm" />}
      {mapa.isError && (
        <Alert color="red" title="Não foi possível carregar o fluxo">
          Tente de novo em instantes. Nada do que está abaixo foi calculado.
        </Alert>
      )}
      {numeros.isError && !candidatas && (
        <Alert color="yellow" title="Não foi possível carregar os números de agora">
          O fluxo abaixo vale; só a contagem de chamados está faltando.
        </Alert>
      )}

      {mapa.data && fluxo && (
        <>
          {visao === 'atendimento' && (
            <Tabs value={aba} onChange={(v) => { setAba((v as AbaDoFluxo) ?? 'atendimento'); setSelecao(null) }}>
              <Tabs.List>
                <Tabs.Tab value="atendimento">Atendimento e aprovação</Tabs.Tab>
                <Tabs.Tab value="mudanca">Mudança por ambiente</Tabs.Tab>
              </Tabs.List>
            </Tabs>
          )}
          {modo === 'lista'
            ? <ListaDoFluxo mapa={mapa.data} fluxo={fluxo} numeros={numeros.data} visao={visao} onSelecionar={setSelecao} />
            : <DiagramaDoFluxo mapa={mapa.data} fluxo={fluxo} numeros={numeros.data} visao={visao} onSelecionar={setSelecao} />}
          <ComoOPrazoCorre mapa={mapa.data} numeros={numeros.data} visao={visao} />
          <DetalheDoFluxo selecao={selecao} mapa={mapa.data} fluxo={fluxo} numeros={numeros.data}
            visao={visao} celular={celular} onFechar={() => setSelecao(null)} />
        </>
      )}
    </Stack>
  )
}

export default ComoFunciona
